import { useState, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Image, File as FileIcon, Loader2, ChevronDown, ChevronRight, History, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useDeliverableFiles,
  useUploadDeliverableFile,
  useDeleteDeliverableFile,
  useDeliverableFileVersions,
  useUploadDeliverableFileVersion,
} from '@/hooks/useDeliverableFiles';
import type { DeliverableFile, DeliverableFileVersion } from '@/hooks/useDeliverableFiles';
import { api } from '@/lib/api';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return Image;
  if (contentType === 'application/pdf') return FileText;
  return FileIcon;
}

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  return 'just now';
}

// ============================================================================
// Version History Panel (per file)
// ============================================================================

function FileVersionHistory({
  deliverableId,
  file,
}: {
  deliverableId: string;
  file: DeliverableFile;
}) {
  const { data: versions } = useDeliverableFileVersions(deliverableId, file.id);
  const uploadVersion = useUploadDeliverableFileVersion();
  const [changeNote, setChangeNote] = useState('');
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  const handleVersionUpload = async (newFile: globalThis.File) => {
    await uploadVersion.mutateAsync({
      deliverableId,
      fileId: file.id,
      file: newFile,
      changeNote: changeNote.trim() || undefined,
    });
    setChangeNote('');
  };

  return (
    <div className="pl-6 pr-2 py-1.5 space-y-1.5">
      {/* Upload new version */}
      <div className="flex items-center gap-2">
        <input
          ref={versionFileInputRef}
          type="file"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) await handleVersionUpload(f);
            e.target.value = '';
          }}
        />
        <Input
          placeholder="What changed? (optional)"
          value={changeNote}
          onChange={(e) => setChangeNote(e.target.value)}
          className="flex-1 h-6 text-[11px]"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[11px] px-2"
          onClick={() => versionFileInputRef.current?.click()}
          disabled={uploadVersion.isPending}
        >
          {uploadVersion.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <Upload className="h-3 w-3 mr-1" /> New Version
            </>
          )}
        </Button>
      </div>

      {/* Version list */}
      <div className="space-y-0.5 max-h-36 overflow-y-auto">
        {(versions || []).map((v: DeliverableFileVersion) => (
          <div
            key={v.id}
            className="flex items-center gap-2 px-1.5 py-1 rounded text-[11px] hover:bg-muted/50 group"
          >
            <span className="font-medium w-6 shrink-0 text-muted-foreground">v{v.version_number}</span>
            <span className="truncate flex-1">
              {v.file_name}
              {v.change_note && (
                <span className="ml-1 text-muted-foreground">— {v.change_note}</span>
              )}
            </span>
            <span className="text-muted-foreground shrink-0">{formatFileSize(v.file_size)}</span>
            {v.uploaded_by && (
              <span className="text-muted-foreground shrink-0 hidden sm:inline">{v.uploaded_by}</span>
            )}
            <span className="text-muted-foreground shrink-0">{timeAgo(v.created_at)}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 opacity-0 group-hover:opacity-100"
              onClick={() => api.deliverables.files.downloadVersion(deliverableId, v.deliverable_file_id, v.id, v.file_name)}
              title="Download this version"
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface DeliverableFileListProps {
  deliverableId: string;
}

export function DeliverableFileList({ deliverableId }: DeliverableFileListProps) {
  const { data: files, isLoading } = useDeliverableFiles(deliverableId);
  const uploadFile = useUploadDeliverableFile();
  const deleteFile = useDeleteDeliverableFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList) return;
    for (const file of Array.from(fileList)) {
      await uploadFile.mutateAsync({ deliverableId, file });
    }
  };

  const handleDownload = async (file: DeliverableFile) => {
    await api.deliverables.files.downloadFile(deliverableId, file.id, file.file_name);
  };

  const handleDelete = (file: DeliverableFile) => {
    deleteFile.mutate({ deliverableId, fileId: file.id });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const toggleVersions = (fileId: string) => {
    setExpandedFileId((prev) => (prev === fileId ? null : fileId));
  };

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground">
        Loading files...
      </div>
    );
  }

  return (
    <div
      className={`px-3 py-2 border-t bg-muted/30 space-y-1 ${
        isDragging ? 'ring-2 ring-primary/30 bg-primary/5' : ''
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragging(false)}
    >
      {/* Section header */}
      <div className="flex items-center gap-1.5 pb-0.5">
        <Paperclip className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Files</span>
        <span className="text-[10px] text-muted-foreground/70">— Upload files directly to this deliverable</span>
      </div>

      {/* File list */}
      {(files || []).map((file) => {
        const Icon = getFileIcon(file.content_type);
        const isExpanded = expandedFileId === file.id;
        return (
          <div key={file.id}>
            <div className="flex items-center gap-2 py-1 group">
              {/* Expand/collapse toggle */}
              <button
                onClick={() => toggleVersions(file.id)}
                className="p-0 h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
                title="Version history"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </button>
              <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs font-medium truncate flex-1">{file.file_name}</span>
              <span className="text-[10px] font-medium text-primary/70 bg-primary/10 rounded px-1 shrink-0">
                v{file.current_version}
              </span>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {formatFileSize(file.file_size)}
              </span>
              {file.uploaded_by && (
                <span className="text-[10px] text-muted-foreground whitespace-nowrap hidden sm:inline">
                  {file.uploaded_by}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {timeAgo(file.created_at)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 opacity-0 group-hover:opacity-100"
                onClick={() => handleDownload(file)}
                title="Download"
              >
                <Download className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 opacity-0 group-hover:opacity-100"
                onClick={() => handleDelete(file)}
                title="Delete"
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
            {/* Expandable version history */}
            {isExpanded && (
              <FileVersionHistory deliverableId={deliverableId} file={file} />
            )}
          </div>
        );
      })}

      {/* Empty state / Upload area */}
      {(!files || files.length === 0) && !uploadFile.isPending && (
        <div className="text-center py-2">
          <p className="text-[11px] text-muted-foreground">
            {isDragging ? 'Drop file here' : 'Drag & drop or click to upload files'}
          </p>
        </div>
      )}

      {/* Upload controls */}
      <div className="flex items-center gap-2 pt-1">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          multiple
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs text-primary hover:text-primary/80"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadFile.isPending}
        >
          {uploadFile.isPending ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-3 w-3 mr-1" />
              Upload file
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
